'use client';
import { twMerge } from 'tailwind-merge';
import {
  Button,
  Avatar,
  Icon,
  LoadingSkeleton,
  Text,
  FormImage,
  MobileHeader,
} from '@ui/components/atoms';
import { BadgeImageInfo } from '@ui/components/moleculas/badge-image-info';
import {
  IBadgeClaimCode,
  IBadgeClaimCreators,
  IBadgeClaimState,
  IBadgeClaimType,
} from '../badge-claim.typings';
import { badgeClaimClassName } from '../badge-claim.style';
import { InputForm, MobileHeaderBackButton } from '@ui/components/moleculas';
import { BadgeType, DynamicRouteBuilder } from '@ui/constants';
import { useState } from 'react';
import { TextWithLinks } from '@ui/components/moleculas/message-link';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useMobile } from '@ui/hooks';
import { Control } from 'react-hook-form';
import { BadgeClaimSubmit } from './badge-claim-info/badge-claim-info-panel/badge-claim-submit';
import { IWhitelabelConfig } from '../../user/creator-details.typings';
import { getLabel } from '../../../../lib/labels';
import type { BadgeCollectionAction } from '@ui/lib/badge-collection-action';

interface IBadgeClaimImage {
  userName: string;
  avatar?: string;
  showAvatar?: boolean;
  name: string;
  image: string;
  caption: string;
  type: BadgeType;
  color: string;
  claimType: IBadgeClaimType;
  badgeState: IBadgeClaimState;
  control?: Control<IBadgeClaimCode>;
  badgeNumber: number;
  badgeAmount?: number;
  perks: string;
  onBack?(): void;
  gotoForum?(): void;
  creatorHasCommunity?: boolean;
  onBadgeLeaderboard?(): void;
  badgeType?: number | null;
  shouldBeGrey?: boolean;
  whitelabelConfig?: IWhitelabelConfig;
  isLoggedIn?: string | null;
  newPostNotifications: any;
  badgeInChallenge?: boolean;
  openChallenge?(): void;
  hasAchieved?: boolean;
  isSubscribed?: boolean;
  creator?: IBadgeClaimCreators;
  claimCodeActive?: boolean;
  badgeOwned?: boolean;
  sponsors?: string[];
  publicLink?: string | null;
  collectionAction?: BadgeCollectionAction;
  isJoiningBadge?: boolean;
  isCatalogueContext?: boolean;
  isClaimExpired?: boolean;
  isLoading?: boolean;
  onClick?(): void;
  badgeId?: string;
}

export function BadgeClaimImage({
  userName,
  name,
  avatar,
  showAvatar,
  caption,
  image,
  type,
  color,
  claimType,
  badgeState = 'default',
  badgeNumber,
  perks,
  badgeAmount,
  shouldBeGrey,
  isLoggedIn,
  onBack,
  onBadgeLeaderboard,
  gotoForum,
  badgeType,
  newPostNotifications,
  creatorHasCommunity,
  badgeInChallenge,
  openChallenge,
  hasAchieved,
  isSubscribed,
  creator,
  claimCodeActive,
  badgeOwned,
  control,
  sponsors,
  publicLink,
  collectionAction,
  isJoiningBadge,
  isCatalogueContext,
  isClaimExpired,
  isLoading,
  onClick,
  whitelabelConfig,
  badgeId,
}: IBadgeClaimImage) {
  const [showSpinner, setShowSpinner] = useState(true);
  const isMobile = useMobile();
  const textParamMap: Record<string, { text: string }> = {
    default: { text: 'Collect Badge to Unlock Perks' },
    collected: { text: 'Badge Perks' },
    'out-of-stock': { text: 'Collect Badge to Unlock Perks' },
    'already-collected': {
      text: 'Badge Perks',
    },
  };
  const router = useRouter();
  const params = useSearchParams();
  const isFromClaim = params.get('claim');
  const isFromCompleted = params.get('completed');
  const isRedeemed = params.get('redeemed');

  const handleImageLoad = () => {
    setShowSpinner(false);
  };
  const isPrizeBadge = badgeType === BadgeType.Prize && !hasAchieved;

  const className = badgeClaimClassName[claimType];

  const handlegoToBadgesClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();

    if (creator?.creator_id) {
      if (isFromClaim) {
        router.push(DynamicRouteBuilder.badges(creator?.name));
      } else if (isFromCompleted) {
        router.refresh();
        router.push(
          `${DynamicRouteBuilder.challenges(
            creator?.name,
          )}?badgeId=${badgeId}&subtab=Completed&t=${Date.now()}`,
        );
      }
    }
  };

  const handleCompletedChallengesClick = () => {
    if (creator?.creator_id) {
      router.refresh();
      router.push(
        `${DynamicRouteBuilder.challenges(
          creator?.name,
        )}?badgeId=${badgeId}&subtab=Completed&t=${Date.now()}`,
      );
    }
  };

  return (
    <FormImage
      className={className.container.image}
      internalClassName={className.container.imageInternal}>
      <MobileHeader className={className.badgeImage.header}>
        {isFromClaim ? (
          <div
            className="text-sm underline text-primary cursor-pointer"
            onClick={handlegoToBadgesClick}>
            My Badges
          </div>
        ) : (
          <MobileHeaderBackButton showButton={true} onBack={onBack} />
        )}
        {userName && (
          <div
            className={
              'flex items-center gap-2 overflow-hidden rounded-full p-2 font-medium bg-input hover:bg-inputActive active:bg-inputActive'
            }>
            <Link href="/settings">
              <Avatar
                size="sm"
                src={avatar}
                displayName={userName}
                className={showAvatar ? '' : 'invisible'}
                isSubscribed={isSubscribed}
              />
            </Link>
          </div>
        )}
      </MobileHeader>
      {badgeState === 'loading' ? (
        <div className="flex h-full flex-col items-center justify-center py-4">
          <LoadingSkeleton className="mb-6 aspect-square w-48 lg:w-64" />
          <LoadingSkeleton className="mb-4 h-12 w-64" />
          <LoadingSkeleton className="h-10 w-60" />
          <LoadingSkeleton className="h-10 w-60 mt-4" />
        </div>
      ) : (
        <div
          className={twMerge(
            'flex h-full flex-1 flex-col items-center justify-center',
            className.badgeImage.container,
          )}>
          <div
            className={`${
              (badgeState === 'go-to-content' || badgeState === 'out-of-stock' || shouldBeGrey) &&
              'opacity-[0.6] grayscale'
            }`}>
            <div className="relative">
              <BadgeImageInfo
                name={name}
                caption={caption}
                image={image}
                type={type}
                color={color}
                badgeAmount={badgeAmount}
                size={claimType === 'default' ? 'xl' : 'l'}
                badgeNumber={badgeNumber}
                showSpinner={showSpinner}
                onImageLoad={handleImageLoad}
              />
              {isRedeemed && (
                <div className="absolute top-6 right-7 z-10 rotate-[-12deg] rounded-sm border border-red-600 bg-transparent px-1.5 py-0.5 text-[8.5px] font-extrabold uppercase tracking-wider text-red-600 shadow-sm opacity-90 md:top-14 md:right-14 md:px-2 md:py-1 md:text-[10px] md:border-2 md:tracking-widest">
                  Redeemed
                </div>
              )}
            </div>
          </div>

          {isMobile && (
            <div className="mb-4">
              {claimCodeActive && control && !badgeOwned && (
                <div className="flex flex-col items-center justify-center my-3">
                  <Text type="m" className="my-2">
                    Enter Unique Code to claim this badge
                  </Text>
                  <div className="md:w-50">
                    <InputForm
                      label=""
                      placeholder="Qstw"
                      form={{
                        control,
                        name: 'claimCode',
                        rules: {
                          required: 'Please enter a valid claim code before collecting the badge.',
                          maxLength: {
                            value: 10,
                            message: 'Badge name should be less than 10 symbols',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              <BadgeClaimSubmit
                hasAchieved={hasAchieved}
                sponsors={sponsors}
                badgeType={badgeType}
                publicLink={publicLink}
                collectionAction={collectionAction}
                isJoiningBadge={isJoiningBadge}
                isCatalogueContext={isCatalogueContext}
                openChallenge={openChallenge}
                isClaimExpired={isClaimExpired}
                badgeState={badgeState}
                isLoading={isLoading}
                onClick={onClick}
                small={true}
              />
            </div>
          )}

          <div className={'flex w-full flex-col items-center justify-center gap-4 pb-8 lg:pb-12'}>
            {perks?.length > 0 && (
              <div className={twMerge('max-w-sm px-6', perks ? '' : 'hidden')}>
                <TextWithLinks
                  text={textParamMap[badgeState]?.text}
                  startsTruncated={true}
                  className="mb-2 text-center font-semibold"
                />
                <div className="min-w-[300px] rounded-md bg-transparent bg-gradient-to-br from-[#8F00FF] to-[#FFA500] p-0.5">
                  <div className="h-full w-full max-w-sm rounded-[14px] bg-white p-4">
                    {/* <Text type="m">{perks}</Text> */}
                    <TextWithLinks
                      text={perks}
                      startsTruncated={true}
                      className="mb-2 text-center font-semibold"
                    />
                  </div>
                </div>
                {badgeInChallenge && !isPrizeBadge && (
                  <div
                    className="flex items-center justify-center flex-col mt-4 mb-4 w-full whitespace-nowrap text-md"
                    onClick={openChallenge}>
                    <span>This badge belongs to a challenge</span>
                    <div className="flex items-center justify-center mt-2">
                      <Icon
                        type="challenge"
                        stroke="#FF600D"
                        className="mr-1 w-5 h-5 md:w-6 md:h-6"
                      />{' '}
                      <span className="text-sm md:text-md font-semibold cursor-pointer text-primary">
                        View challenge
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile buttons section - Stacked vertically */}
            <div className="flex flex-col gap-3 w-full px-6 lg:hidden items-center">
              {/* 1. Completed Challenges - Only show when coming from completed */}
              {isFromCompleted && creator?.creator_id && (
                <Button
                  type="primary"
                  className="w-[90%] md:w-80 text-sm whitespace-nowrap bg-primary hover:bg-primary/90"
                  onClick={handleCompletedChallengesClick}>
                  Completed Challenges
                </Button>
              )}

              {/* 2. Community */}
              {creatorHasCommunity && (
                <div className="relative w-[90%] md:w-80">
                  {newPostNotifications && newPostNotifications?.unread_count > 0 && (
                    <div className="w-5 h-5 rounded-full bg-primary text-white absolute -top-1 -right-1 flex items-center justify-center text-xs z-10">
                      {newPostNotifications?.unread_count}
                    </div>
                  )}
                  <Button
                    type="primary"
                    className="w-full text-sm whitespace-nowrap bg-black hover:bg-black/95"
                    onClick={gotoForum}>
                    <Icon type="community" className="mr-2" /> Community
                  </Button>
                </div>
              )}

              {/* 3. Ranking */}
              {onBadgeLeaderboard && (
                <Button
                  type="secondary"
                  className="w-[90%] md:w-80 text-sm whitespace-nowrap"
                  onClick={onBadgeLeaderboard}>
                  <Icon type="leaderboard" className="mr-2" /> Ranking
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </FormImage>
  );
}
